import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './OptimizerCostsResults.css';
import { OptimizationOption } from '../../../../mocks/optimizationOptions';
interface RowItem {
  id: number;
  title: string;
  averagePrice: number;
  isChat: string;
  optimizationOptions: OptimizationOption[];
  optCostsResults: OptCostResult[];
}
export type RowData = RowItem[];
interface OptCostResult {
  id: number;
  title: string;
  contact: string;
  price: number;
  enabled: boolean;
}
function createData(
  id: number,
  title: string,
  averagePrice: number,
  optCostsResults: OptCostResult[],
) {
  return {
    id,
    title,
    averagePrice,
    optCostsResults,
  };
}
function Row(
  { row: { title, averagePrice, optCostsResults, id: rowId },
    records,
    setRecordsData,
  }: {
    row: ReturnType<typeof createData>,
    records: RowData,
    setRecordsData: (data: RowData) => void,
  }) {
  const { t } = useTranslation('optimizer');
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<readonly number[]>((() => {
    const selected: number[] = [];
    optCostsResults?.forEach((result: { enabled: boolean, id: number }) => {
      if (result.enabled) {
        selected.push(result.id);
      }
    });
    return selected;
  }));
  const handleClick = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    setSelected([id]);
    records.forEach(item => {
      if (item.id === rowId && item.optCostsResults) {
        item.optCostsResults.forEach(option => {
          option.enabled = option.id === id;
        });
      }
    });
    setRecordsData(JSON.parse(JSON.stringify(records)));
  };
  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {optCostsResults && optCostsResults.length && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell component="th" scope="row">
          {title}
        </TableCell>
        <TableCell align="right">{averagePrice}$</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t('table.alternativesOnMarket')}
              </Typography>
              {optCostsResults && optCostsResults.length > 0 ? (
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell><strong>{t('table.title')}</strong></TableCell>
                      <TableCell align="right"><strong>Contact</strong></TableCell>
                      <TableCell align="right"><strong>Price ($)</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {optCostsResults.map(({ id, title, contact, price }) => {
                      const isItemSelected = selected.includes(id);
                      return (
                        <TableRow key={id}>
                          <TableCell padding="checkbox">
                            <Radio
                              checked={isItemSelected}
                              onChange={(e) => handleClick(e, id)}
                              value={id.toString()}
                              name="radio-buttons"
                              inputProps={{ 'aria-labelledby': id.toString() }}
                            />
                          </TableCell>
                          <TableCell component="th" scope="row">{title}</TableCell>
                          <TableCell align="right">{contact}</TableCell>
                          <TableCell align="right">{price}$</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body1" sx={{ mt: 1, ml: 2, fontStyle: 'italic', color: 'gray' }}>
                  {t('table.noResultsFound')}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}
export default function OptimizerCostsResults() {
  const { t } = useTranslation('optimizer');
  const navigate = useNavigate();
  const [records, setRecords] = React.useState<RowData>(() => {
    const recordsData = localStorage.getItem('optCostsResults') || '[]';
    const initialValue = JSON.parse(recordsData);
    return initialValue || [];
  });
  if (!records.length) {
    navigate('/optimizer-list');
  }
  const prevTotal = React.useMemo(() => {
    return records.reduce(
      (accumulator, { averagePrice }) => accumulator + +averagePrice,
      0,
    );
  }, records);
  const total = React.useMemo(() => {
    return records.reduce(
      (accumulator, { averagePrice, optCostsResults }) => {
        let priceNum = +averagePrice;
        if (optCostsResults && optCostsResults.length) {
          optCostsResults.forEach(({ enabled, price }) => {
            if (enabled) {
              priceNum = price;
            }
          });
        }
        return accumulator + priceNum;
      },
      0,
    );
  }, records);
  return (
    <>
      <h1 className="data-header">{t('table.expandAndSelect')}</h1>
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{t('table.title')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{t('table.averagePrice')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map(({ id, title, averagePrice, optCostsResults }) => (
              <Row
                key={id}
                row={createData(id, title, averagePrice, optCostsResults)}
                records={records}
                setRecordsData={setRecords}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <section className="total-amount">
        <strong>{t('table.total')}</strong>
        {!!(prevTotal - total) && (
          <span className="prev-total-amount">
            {prevTotal.toFixed(3)}$
          </span>
        )}
        <span className="new-total-amount">
          {total.toFixed(3)}$
        </span>
      </section>
      {!!(prevTotal - total) && (
        <section className="total-amount-savigs">
          <strong>{t('table.savings')}:</strong> +{(prevTotal - total).toFixed(3)}$
        </section>
      )}
    </>
  );
}
